import { Assets } from 'pixi.js';
import { AudioManager } from '../systems/AudioManager.js';
import * as PIXI from 'pixi.js';

// Asset bundles configuration
export const ASSET_BUNDLES = {
    images: {
        name: 'game-assets',
        assets: {
            // Player assets
            player: 'assets/images/player/player.png',
            'player-spritemap': 'assets/images/player/player-spritemap.png',
            // Plant assets
            bush: 'assets/images/plants/bush.png',
            mushroom: 'assets/images/plants/mushroom.png',
            herb: 'assets/images/plants/herb.png',
            cactus: 'assets/images/plants/cactus.png',
            flower: 'assets/images/plants/flower.png',
            pine: 'assets/images/plants/pine.png',
            // Biome assets
            mountains: 'assets/images/biomes/mountains.png',
            forest: 'assets/images/biomes/forest.png',
            beach: 'assets/images/biomes/beach.png',
            plains: 'assets/images/biomes/plains.png',
            water: 'assets/images/biomes/water.png',
            // Animal assets
            rabbit: 'assets/images/animals/mammals/rabbit.png',
            goat: 'assets/images/animals/mammals/goat.png',
            wolf: 'assets/images/animals/mammals/wolf.png',
            bee: 'assets/images/animals/insects/bee.png',
            bee_eater: 'assets/images/animals/birds/bee_eater.png',
            hawk: 'assets/images/animals/birds/hawk.png',
            // Structure assets
            hive: 'assets/images/structures/hive.png',
            // Rock assets
            rock_small: 'assets/images/rocks/rock_small.png',
            rock_medium: 'assets/images/rocks/rock_medium.png',
            rock_large: 'assets/images/rocks/rock_large.png'
        }
    },
    audio: {
        menu: {
            music: 'assets/audio/menu-music.wav',
            buttonClick: 'assets/audio/button-click.mp3',
            buttonHover: 'assets/audio/button-hover.mp3'
        },
        game: {
            // Add game audio assets here
            footsteps: 'assets/audio/footsteps.mp3',
            waterSplash: 'assets/audio/water-splash.mp3',
            animalSounds: 'assets/audio/animal-sounds.mp3'
        }
    }
};

export class AssetManager {
    constructor() {
        console.log('[ASSET_MANAGER] Initializing AssetManager');
        this.audioManager = new AudioManager();
        this.loaded = false;
        this.audioLoaded = false;
        this.sprites = new Map();
    }

    async loadAllAssets() {
        try {
            console.log('[ASSET_MANAGER] Starting to load all assets');
            
            // Initialize Assets system only if not already initialized
            if (!Assets.cache.get()) {
                console.log('[ASSET_MANAGER] Initializing Assets system');
                await Assets.init({
                    manifest: {
                        bundles: [ASSET_BUNDLES.images]
                    }
                });
            } else {
                console.log('[ASSET_MANAGER] Assets system already initialized');
            }

            // Skip audio loading for now to prevent hanging
            console.log('[ASSET_MANAGER] Skipping audio loading (disabled for debugging)');
            // await this.loadAudioAssets();

            this.loaded = true;
            console.log('[ASSET_MANAGER] All assets loaded successfully');
            return true;
        } catch (error) {
            console.error('[ASSET_MANAGER] Failed to load assets:', error);
            throw error;
        }
    }

    async loadAudioAssets() {
        try {
            console.log('[ASSET_MANAGER] Loading menu audio...');
            // Load menu audio
            await this.audioManager.loadSound(ASSET_BUNDLES.audio.menu.music, 'menu_music').catch(error => {
                console.warn('[ASSET_MANAGER] Failed to load menu music, continuing without it:', error);
            });
            
            await this.audioManager.loadSound(ASSET_BUNDLES.audio.menu.buttonClick, 'button_click').catch(error => {
                console.warn('[ASSET_MANAGER] Failed to load button click sound, continuing without it:', error);
            });
            
            await this.audioManager.loadSound(ASSET_BUNDLES.audio.menu.buttonHover, 'button_hover').catch(error => {
                console.warn('[ASSET_MANAGER] Failed to load button hover sound, continuing without it:', error);
            });

            console.log('[ASSET_MANAGER] Loading game audio...');
            // Load game audio
            await this.audioManager.loadSound(ASSET_BUNDLES.audio.game.footsteps, 'footsteps').catch(error => {
                console.warn('[ASSET_MANAGER] Failed to load footsteps sound, continuing without it:', error);
            });
            
            await this.audioManager.loadSound(ASSET_BUNDLES.audio.game.waterSplash, 'water_splash').catch(error => {
                console.warn('[ASSET_MANAGER] Failed to load water splash sound, continuing without it:', error);
            });
            
            await this.audioManager.loadSound(ASSET_BUNDLES.audio.game.animalSounds, 'animal_sounds').catch(error => {
                console.warn('[ASSET_MANAGER] Failed to load animal sounds, continuing without it:', error);
            });
            
            this.audioLoaded = true;
            console.log('[ASSET_MANAGER] Audio assets loading complete');
        } catch (error) {
            console.warn('[ASSET_MANAGER] Some audio assets failed to load, but continuing:', error);
        }
    }

    getAudioManager() {
        return this.audioManager;
    }

    isLoaded() {
        return this.loaded;
    }

    isAudioLoaded() {
        return this.audioLoaded;
    }

    destroy() {
        console.log('[ASSET_MANAGER] Destroying AssetManager');
        if (this.audioManager) {
            this.audioManager.destroy();
        }
        this.loaded = false;
        this.audioLoaded = false;
    }

    loadSprites() {
        console.log('[ASSET_MANAGER] Loading sprites into sprite map');
        console.log('[ASSET_MANAGER] Available assets in cache:', Assets.cache.get());
        
        // Get the paths from ASSET_BUNDLES
        const beePath = ASSET_BUNDLES.images.assets.bee;
        const beeEaterPath = ASSET_BUNDLES.images.assets.bee_eater;
        const rabbitPath = ASSET_BUNDLES.images.assets.rabbit;
        const hawkPath = ASSET_BUNDLES.images.assets.hawk;
        
        console.log('[ASSET_MANAGER] Loading textures from paths:', { beePath, beeEaterPath, rabbitPath, hawkPath });
        
        // Use Assets.get() to get textures from the cache
        const beeTexture = Assets.get(beePath);
        const beeEaterTexture = Assets.get(beeEaterPath);
        const rabbitTexture = Assets.get(rabbitPath);
        const hawkTexture = Assets.get(hawkPath);
        
        if (beeTexture) {
            console.log('[ASSET_MANAGER] Loaded bee texture');
            this.sprites.set('bee', beeTexture);
        } else {
            console.warn('[ASSET_MANAGER] Failed to load bee texture from path:', beePath);
        }
        
        if (beeEaterTexture) {
            console.log('[ASSET_MANAGER] Loaded bee-eater texture');
            this.sprites.set('bee_eater', beeEaterTexture);
        } else {
            console.warn('[ASSET_MANAGER] Failed to load bee-eater texture from path:', beeEaterPath);
        }

        if (rabbitTexture) {
            console.log('[ASSET_MANAGER] Loaded rabbit texture');
            this.sprites.set('rabbit', rabbitTexture);
        } else {
            console.warn('[ASSET_MANAGER] Failed to load rabbit texture from path:', rabbitPath);
        }

        if (hawkTexture) {
            console.log('[ASSET_MANAGER] Loaded hawk texture');
            this.sprites.set('hawk', hawkTexture);
        } else {
            console.warn('[ASSET_MANAGER] Failed to load hawk texture from path:', hawkPath);
        }
    }
} 